import { ApplicationProgressModel } from "./application-progress.model";

export interface IApplicationInfoModel {
  formId: string;
  form?: string;
  formType: string;
  suggestedNames: string[];
  modifiedDate: string;
  submittedDate:string;
  status: string;
  negotiator: string;
  caseSysId: string;
  lockedById: string;
  lastLockedTime: string;
}

export class ApplicationInfoModel implements IApplicationInfoModel {
  private static readonly maxNamesLength: number = 32;
  private static readonly maxDetailsLength: number = 28;
  private _formId: string;
  private _form?: string;
  private _formType: string;
  private _suggestedNames: string[];
  private _suggestedNamesText: string;
  private _modifiedDate: string;
  private _submittedDate: string;
  private _status: string;
  private _negotiator: string;
  private _progress: ApplicationProgressModel;
  private _draftDetailsText: string;
  private _submittedDetailsText: string;
  private _caseSysId: string;
  private _lockedById: string;
  private _lastLockedTime: string;
  

  constructor(appModel?: IApplicationInfoModel) {
    if(!appModel) {
      appModel = {
        formId: "",
        form: "",
        formType: "",
        suggestedNames: [],
        modifiedDate:  "",
        submittedDate: "",
        status: "",
        negotiator: "",
        caseSysId: "",
        lockedById: "",
        lastLockedTime: "" 
      }
    }

    this._formId = appModel.formId;
    this._form = appModel.form;
    this._formType = appModel.formType;
    this._suggestedNames = appModel.suggestedNames;
    this._suggestedNamesText = this.generateSuggestedNamesText(appModel.suggestedNames);
    this._modifiedDate = appModel.modifiedDate;
    this._submittedDate = appModel.submittedDate;
    this._negotiator = appModel.negotiator;
    this._progress = new ApplicationProgressModel(appModel.status);
    this._status = this._progress?.status;
    this._draftDetailsText = this.generateDetailsText(appModel.suggestedNames, appModel.modifiedDate);
    this._submittedDetailsText = this.generateDetailsText(appModel.suggestedNames, appModel.submittedDate);
    this._caseSysId = appModel.caseSysId;
    this._lockedById = appModel.lockedById;
    this._lastLockedTime = appModel.lastLockedTime;
  }

  private generateSuggestedNamesText(names: string[]): string {
    if(!names) {
      return '';
    }

    let nameStr = names.join(', ');

    if(this._status !== 'Draft') {
      return nameStr;
    }

    if(nameStr.length > ApplicationInfoModel.maxNamesLength) {
      nameStr = nameStr.substring(0, ApplicationInfoModel.maxNamesLength) + '...';
    }
    return nameStr;
  }

  private generateDetailsText(names: string[], date: string): string {
    if(!names) {
      return date;
    }

    let detailsStr = date + ' - ' + names.join(', ');

    if(detailsStr.length > ApplicationInfoModel.maxDetailsLength) {
      detailsStr = detailsStr.substring(0, ApplicationInfoModel.maxDetailsLength) + '...';
    }
    return detailsStr;
  }

  get formId() {
    return this._formId;
  }

  get formType() {
    return this._formType;
  }

  get form() {
    return this._form;
  }

  get suggestedNames() {
    return this._suggestedNames;
  }

  get suggestedNamesText() {
    return this._suggestedNamesText;
  }

  get modifiedDate() {
    return this._modifiedDate;
  }

  get submittedDate() {
    return this._submittedDate;
  }

  get status() {
    return this._status
  }

  get negotiator() {
    return this._negotiator;
  }

  get progress() {
    return this._progress;
  }

  get progressValue() {
    return this._progress?.progress;
  }

  get draftDetailsText() {
    return this._draftDetailsText;
  }

  get submittedDetailsText() {
    return this._submittedDetailsText;
  }
  get caseSysId() {
    return this._caseSysId;
  }

  get lockedById() {
    return this._lockedById;
  }

  get lastLockedTime() {
    return this._lastLockedTime;
  }
  
  public static parseValues(json: any) {
    if (!json) {
      return new ApplicationInfoModel();
    }

    const app = {
      formId: json.formId,
      formType: json.formType,
      suggestedNames: json.suggestedNames,
      modifiedDate: json.modifiedDate,
      submittedDate: json.submittedDate,
      status: json.status,
      negotiator: json.negotiator,
      caseSysId: json.caseSysId,
      lockedById: json.u_locked_by_sso_id,
      lastLockedTime: json.u_last_locked_activity_time
    };

    return new ApplicationInfoModel(app);
  }

  public static parseAPIValues(json: any) {
    if (!json) {
      return new ApplicationInfoModel();
    }
    let openedDateStr = '';
    if (json.opened_at) {
       const openedDate = new Date(json.opened_at); // assumes input is like "2025-04-28" 
       const month = String(openedDate.getMonth() + 1).padStart(2, '0'); // Months are 0-indexed
       const day = String(openedDate.getDate()).padStart(2, '0');
       const year = openedDate.getFullYear();
       openedDateStr = month + '/' + day + '/' + year;
    }

    let modifiedDateStr = '';
    if (json.sys_updated_on) {
       const openedDate = new Date(json.sys_updated_on); // assumes input is like "2025-04-28" 
       const month = String(openedDate.getMonth() + 1).padStart(2, '0'); // Months are 0-indexed
       const day = String(openedDate.getDate()).padStart(2, '0');
       const year = openedDate.getFullYear();
       modifiedDateStr = month + '/' + day + '/' + year;
    }

    const app = {
      formId: json.sys_id,
      form: json.u_form_type?.toLowerCase().includes('form') ? json.u_form_type.split('Form')[1] : json.u_form_type,
      formType: json.u_form_type,
      //suggestedNames: json.u_suggested_names_in_order_of_preference_for_single_entity?.split(","),
      suggestedNames: json.u_form_type?.includes('D') ? [json.u_name_for_which_you_are_requesting_a_revision] : 
        json.u_suggested_names_in_order_of_preference_for_single_entity?.split(","),
      modifiedDate: modifiedDateStr, 
      submittedDate: modifiedDateStr,
      status: json.u_case_status,
      negotiator: json.negotiator?.name,
      caseSysId: json.caseSysId,
      lockedById: json.u_locked_by_sso_id,
      lastLockedTime: json.u_last_locked_activity_time
    };

    return new ApplicationInfoModel(app);
  }

}
